import {NextSeo} from "next-seo";
import {Header} from "@/components/Header";
import HeaderSimple from "@/components/HeaderSimple";
import {Team} from "@/components/Team";
import Office from "@/components/Office";
import {CallToAction} from "@/components/CallToAction";
import {Footer} from "@/components/Footer";
import Image from "next/image";
import milouImage from '@/images/cases/newman/milou.jpg'
import newmanImage from '@/images/cases/newman/newman.jpg'
import newman2Image from '@/images/cases/newman/newman2.jpg'
import emailImage from '@/images/cases/newman/email.png'
import appImage from '@/images/cases/newman/app.png'
import menciaImage from '@/images/cases/mencia/gebouw.jpg'
import ianImage from '@/images/cases/mencia/ian.jpg'
import Link from "next/link";

export default function MenciaDeMendozaLyceum() {
    return (
        <>
            <NextSeo
                title="Interview Ian Lammerse, Mencia de Mendoza Lyceum in Breda"
                description="Het Mencia de Mendoza Lyceum maakt voor het faciliteren van bijles tussen leerlingen onderling gebruik van het platform Bijlesplein. En met succes."
            />
            <Header/>
            <main>
                <div className="bg-white px-6 py-10 lg:py-24 lg:px-8">
                    <div
                        className="mx-auto max-w-6xl text-base leading-7 text-gray-700 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <p className="text-base font-semibold leading-7 text-blue-600">
                                Interview Ian Lammerse, Mencia de Mendoza Lyceum in Breda
                            </p>
                            <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                “Dit is een compleet plaatje als het gaat om vraag en aanbod van bijles tussen
                                leerlingen onderling”
                            </h1>
                            <p className="mt-6 text-lg leading-8">
                                Ian Lammerse is onderwijsassistent en leerlingbegeleider op het Mencia de Mendoza Lyceum
                                in Breda. Het Mencia is sinds begin 2023 gebruiker van Bijlesplein. Ian is de
                                contactpersoon voor het platform en wij interviewden hem over de ervaringen met
                                Bijlesplein. “Voorheen werkten we met Excellijsten. Het organiseren van bijles ging als
                                volgt: een leerling gaf bij de vakdocent of mentor aan dat hij of zij op zoek was naar
                                bijles. De docent/mentor pakte de Excellijst erbij en zorgde voor de verbinding tussen
                                de lentor (tutor) en de leerling die bijles wilde krijgen. Dat vroeg best veel werk van
                                de docent. Met Bijlesplein maken leerlingen nu zelf de verbinding en we zien dat dat
                                goed werkt.”
                            </p>
                        </div>
                        <div className="relative">
                            <div className="relative mx-auto max-w-prose text-base lg:max-w-none">
                                <figure>
                                    <div className="aspect-h-12 aspect-w-12 lg:aspect-none">
                                        <Image className="rounded-lg object-cover object-center shadow-lg"
                                               src={ianImage} alt="Foto van Ian Lammerse"/>
                                    </div>
                                    <figcaption className="mt-3 flex text-sm text-gray-500">
                                        <svg className="h-5 w-5 flex-none text-gray-400" viewBox="0 0 20 20"
                                             fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd"
                                                  d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z"
                                                  clipRule="evenodd"/>
                                        </svg>
                                        <span className="ml-2">Foto van Ian Lammerse</span>
                                    </figcaption>
                                </figure>
                            </div>
                        </div>
                    </div>

                    <div className="mx-auto max-w-3xl text-base leading-7 text-gray-700">

                        <div className="mt-10 max-w-2xl">
                            <p className>
                                Het Mencia de Mendoza Lyceum heeft altijd gewerkt met lentoren, leerlingen die elkaar
                                lesgeven. “Het gebeurt wel eens dat docenten dat zelf doen, bijvoorbeeld het uitleggen
                                van een bepaalde opdracht of onderwerp. Maar de echte bijles wordt voornamelijk door
                                leerlingen zelf gedaan.”
                            </p>

                            <p className="mt-8">
                                Door gebruik te maken van lentoren kun je op een laagdrempelige manier bijles
                                organiseren. En het is bewezen effectief. “Bijlesplein helpt ons omdat het heel
                                makkelijk wordt gemaakt om contact tussen leerlingen onderling te leggen. Vraag en
                                aanbod komen samen.”
                            </p>

                            <p className="mt-8">
                                “Door leerlingen elkaar bijles te laten geven hoeven leerlingen die bijles nodig hebben
                                niet de stap naar een dure bijlespartij te maken. Op deze manier kunnen we de
                                kansenongelijkheid op financieel vlak zo laag mogelijk houden. Leerlingen hebben bij ons
                                zelf in de hand wat ze voor een bijles vragen. Dat kan €5, €7,50 of €10 per uur zijn.”
                            </p>

                            <p className="mt-8">
                                “Als school weten we lang niet altijd hoe de situatie thuis is. Ik merk dat zeker
                                leerlingen in de bovenbouw een volwassen insteek hebben. Zij hebben vaak een eigen
                                bijbaantje en betalen daarmee de bijles zelf. Wanneer zij commerciële partijen moeten
                                gaan betalen, is dat bijna niet haalbaar. De drempel wordt nu een stuk lager gemaakt
                                voor leerlingen.”
                            </p>

                            <p className="mt-8">
                                “Door het portal Bijlesplein bieden wij als school een hele makkelijke service aan
                                leerlingen. Zij hebben zelf in de hand om de stap naar bijles te zetten om bijvoorbeeld
                                toch over te gaan of een examen te halen. Een gesprek met een docent of mentor is in
                                sommige gevallen minder uitnodigend. En we zien dat het werkt. Je ziet gebeuren dat
                                leerlingen volledig op eigen initiatief die bijles regelen.”
                            </p>

                            <figure className="mt-16">
                                <Image className="aspect-video rounded-xl bg-gray-50 object-cover"
                                       src={menciaImage}
                                       alt="Het Mencia de Mendoza Lyceum"/>
                                <figcaption className="mt-4 flex gap-x-2 text-sm leading-6 text-gray-500">
                                    <svg className="mt-0.5 h-5 w-5 flex-none text-gray-300" viewBox="0 0 20 20"
                                         fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd"
                                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                                              clipRule="evenodd"/>
                                    </svg>
                                    Het Mencia de Mendoza Lyceum
                                </figcaption>
                            </figure>

                            <h2 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
                                Hoe verloopt de samenwerking met Bijlesplein?
                            </h2>
                            <p className="mt-6">
                                De samenwerking tussen het Mencia en Bijlesplein is gestart met Wouter Jagers, de
                                TTO-afdelingsleider en met Rob van Gils samen. “We zijn vorig kalenderjaar gestart en
                                hebben met de bestaande lentoren een test gedaan met Bijlesplein. Vanaf het moment dat
                                de site voor ons klaar was gemaakt om te gaan gebruiken, ben ik de contactpersoon
                                geworden voor alles wat met Bijlesplein te maken heeft. Collega’s en leerlingen weten
                                mij te vinden als het gaat over de portal.”
                            </p>

                            <p className="mt-8">
                                “De basis om Bijlesplein te gaan gebruiken is enerzijds het goede contact en het
                                vertrouwen in elkaar. Daarnaast is onze school overtuigd van de werking van het
                                platform. Er zitten veel voordelen aan. De drempel ligt lager voor leerlingen om bijles
                                te zoeken of aan te bieden. Ze hebben het in eigen hand en we zien dat het werkt.”
                            </p>

                            <p className="mt-8">
                                “Het is heel gebruiksvriendelijk waardoor leerlingen via hun eigen laptop of mobiel zich
                                makkelijk kunnen aanmelden en een match kunnen zoeken. En het gaat veel sneller. In
                                plaats van dat je naar de mentor moet stappen, die moet in een bestandje duiken en de
                                leerling moet wachten op een match. Het is nu veel sneller geregeld. En een mentor heeft
                                het druk genoeg. Bijlesplein legt de pro activiteit bij een leerling neer. Wanneer
                                zijzelf de stappen nemen en zelf snel reageren, kunnen ze bij wijze van dezelfde dag nog
                                aan de slag zijn. Dat gaat sneller dan met het Excelbestand via de mentor of docent.”
                            </p>

                            <p className="mt-8">
                                “In het begin verliep het inloggen niet helemaal soepel. Het is gekoppeld aan Magister.
                                Leerlingen die dan met een ander mailadres probeerde in te loggen kregen geen
                                verificatiemail. Samen met Bijlesplein komen we altijd snel tot een oplossing. Het is
                                heel persoonlijk en vaak snel opgelost. Ook bij nieuwe ideeën trouwens.”
                            </p>

                            <h2 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
                                Complete plaatje
                            </h2>

                            <p className="mt-8">
                                “Ik zou Bijlesplein zeker aanraden aan andere scholen. De voordelen heb ik eerder al
                                genoemd. Het maakt het makkelijk voor leerlingen én de school en het is een goedkopere
                                oplossing om bijles aan te kunnen bieden. Leerlingen hebben het in eigen hand en de
                                school hoeft minder tijd te besteden aan de organisatie ervan. Wat mij betreft is dit
                                een compleet plaatje als het gaat om vraag en aanbod van bijles tussen leerlingen
                                onderling.”
                            </p>

                            <h2 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
                                Ook benieuwd naar de mogelijkheden van Bijlesplein voor jouw school?
                            </h2>

                            <p className="mt-8">
                                Neem <Link href="/contact">contact</Link> met ons op of vraag direct een <Link
                                href="/demo">gratis demo</Link> aan.
                            </p>

                        </div>


                    </div>
                </div>

                <CallToAction/>
            </main>
            <Footer/>
        </>
    )
}
